import { Link } from "react-router-dom";

export const TERMS_PATH = "/taisykles";
export const PRIVACY_PATH = "/privatumas";
export const CONTACTS_PATH = "/kontaktai";

/**
 * The legal footer every page carries: „Taisyklės", „Privatumas", „Kontaktai".
 *
 * The privacy policy has to be reachable from every page, and the seller's
 * details (behind „Kontaktai") have to be on the site — so this is ONE
 * component, used by every shell: the public pages, the auth pages, the
 * grown-up sidebar and the learner's shell. One place for the paths and the
 * words, so they cannot drift between them.
 *
 * Two shapes:
 * - default — a full-width `<footer>` strip, placed last inside a page's
 *   `min-h-screen` container. `sticky top-[100vh]` keeps it at the bottom of
 *   the screen on a short page (a login card) and in the normal flow on a long
 *   one, without asking every page to become a flex column — which would
 *   shrink-wrap the `mx-auto` public header and move its buttons.
 * - `inline` — the links alone, for a footer that already exists around them
 *   (the landing page's, the sidebar's, the legal pages' own).
 *
 * `fullWidth` lines the links up with a shell whose content runs edge to edge
 * (the learner's) instead of with the public pages' centred column.
 */
export function LegalFooter({
  className = "",
  inline = false,
  fullWidth = false,
}: {
  className?: string;
  inline?: boolean;
  fullWidth?: boolean;
}) {
  const links = (
    <nav
      aria-label="Teisinė informacija"
      className={`inline-flex flex-wrap items-center gap-x-4 gap-y-1 ${inline ? className : ""}`}
    >
      <Link to={TERMS_PATH} className="underline underline-offset-4">
        Taisyklės
      </Link>
      <Link to={PRIVACY_PATH} className="underline underline-offset-4">
        Privatumas
      </Link>
      <Link to={CONTACTS_PATH} className="underline underline-offset-4">
        Kontaktai
      </Link>
    </nav>
  );

  if (inline) return links;

  return (
    <footer className={`sticky top-[100vh] border-t text-sm text-muted-foreground ${className}`}>
      <div className={fullWidth ? "flex px-4 py-4 md:px-6" : "mx-auto flex max-w-6xl px-4 py-4 sm:px-6"}>
        {links}
      </div>
    </footer>
  );
}
